import { Link, router, usePage } from '@inertiajs/react'
import { ReactNode, useState } from 'react'

import BagDrawer from './BagDrawer'
import Tracker from './Tracker'
import { Wordmark } from './Wordmark'
import { ThemeToggle } from './ThemeToggle'
import { SellerDetails } from './SellerDetails'
import { CurrencySwitch } from './CurrencySwitch'
import { ArrowLeftIcon, BagIcon } from './Icons'
import { useTranslation } from '@/lib/i18n'
import { trackEvent } from '@/lib/track'
import { cn } from '@/lib/utils'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Toaster } from '@/components/ui/sonner'
import type { SharedProps } from '@/types'

interface LayoutProps {
  children: ReactNode
  /** Spec 09 · "Back — PDP and legal pages." Centred mark, back label
   *  left, bag right. */
  showBack?: boolean
  /** V3 · PDP — the header floats over the hero as translucent chips:
   *  wordmark chip left; theme and bag chips right. */
  overlayHeader?: boolean
  /** V3 · landing — on a phone the mark is the only chip: the theme
   *  and the bag come off, and the footer carries what they did. The
   *  desktop hero keeps the full set. */
  hideHeaderControlsOnMobile?: boolean
  /** 1c · landing — a wide window gets the sheet's own bar instead of
   *  the floating chips: the lines left, the mark centred, the
   *  switches and the bag right, over one hairline. The phone keeps
   *  the chips, which sit on its full-bleed photo. */
  stripNav?: boolean
  /** A scrolling shop notice belongs above the header, outside the product content. */
  announcement?: ReactNode
  /** The phone landing keeps only its floating tiles visible as content scrolls under them. */
  transparentHeader?: boolean
}

/**
 * Spec 09 · Regions.
 *
 * Header → main → footer, with the bag drawer and toast
 * outlet hung off the root. The header is 68px (56 mobile), hairline
 * bottom, sticky at z 10. There is no menu overlay any more: the shop
 * is two pages deep, so the footer carries the links, and the language
 * and currency switches live there on a phone.
 */
export default function Layout({
  children,
  showBack = false,
  overlayHeader = false,
  hideHeaderControlsOnMobile = false,
  stripNav = false,
  announcement,
  transparentHeader = false,
}: LayoutProps) {
  const { cart } = usePage<SharedProps>().props
  const [bagOpen, setBagOpen] = useState(false)

  const openBag = () => {
    trackEvent('open_cart', { meta: { items: cart.count, total: cart.total } })
    setBagOpen(true)
  }

  return (
    <>
      <Tracker />
      {announcement}

      {showBack && !overlayHeader ? (
        <header
          className={cn(
            'sticky top-0 z-10 border-b border-hairline bg-background',
            'flex items-center justify-between gap-4 md:gap-6',
            'h-14 px-4 md:h-17 md:px-8'
          )}
        >
          <BackHeader onOpenBag={openBag} count={cart.count} />
        </header>
      ) : (
        <>
          {stripNav && <StripNav onOpenBag={openBag} count={cart.count} />}
          <ChipHeader
            floating={overlayHeader}
            transparent={transparentHeader}
            hideControlsOnMobile={hideHeaderControlsOnMobile}
            onOpenBag={openBag}
            count={cart.count}
            // The bar takes over the moment there is room for it.
            className={stripNav ? 'min-[560px]:hidden' : undefined}
          />
        </>
      )}

      <main>{children}</main>

      <SiteFooter />

      <BagDrawer open={bagOpen} onOpenChange={setBagOpen} cart={cart} />
      <Toaster />
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Headers                                                             */
/* ------------------------------------------------------------------ */

/* V3 · chip header — translucent blocks on the page or over the hero.
   The wordmark chip goes home; theme and bag ride as square chips on
   the right, hover-inverting to ink.

   Every page but the back-header ones wears it. On the PDP it floats
   over the hero media (`floating`); everywhere else it is an ordinary
   sticky bar on the page background, keeping its hairline underline —
   but the chips, and above all the wordmark's position, are the same
   in both, so the mark does not jump as you move between pages.

   The landing floats too, over its own hero. On a phone it goes one
   step further (`hideControlsOnMobile`): the mark is the only chip,
   and everything it drops — the theme, the bag, the switches — the
   footer carries. */

const chipClass = cn(
  'flex size-[46px] cursor-pointer items-center justify-center rounded-xl border border-hairline',
  'bg-[color:var(--sss-chip)] text-ink backdrop-blur-[10px]',
  'transition-colors duration-200 hover:border-[color:var(--sss-ink)] hover:bg-ink hover:text-ink-inverse'
)

function ChipHeader({
  floating,
  transparent,
  hideControlsOnMobile,
  onOpenBag,
  count,
  className,
}: {
  /** PDP and landing: fixed over the hero, no bar, no underline. */
  floating: boolean
  transparent: boolean
  /** Landing: below the phone stop the mark is the only chip left. */
  hideControlsOnMobile: boolean
  onOpenBag: () => void
  count: number
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <header
      className={cn(
        'z-10 grid grid-cols-[1fr_auto_1fr] items-start gap-3',
        // Twice the page frame, flat at every width: the chip keeps the
        // same margin from the top as from the left, and keeps it while
        // the window is dragged. On the PDP that lands it exactly one
        // frame inside the hero card's corner.
        'p-[calc(var(--sss-frame)*2)]',
        floating
          ? 'pointer-events-none fixed inset-x-0 top-0'
          : transparent
            ? 'pointer-events-none sticky top-0'
            : 'sticky top-0 border-b border-hairline bg-background',
        className
      )}
    >
      {/* Balanced side columns keep the logo centered even when the bag is empty. */}
      <Link
        href="/"
        aria-label={t('layout.brand')}
        className={cn(
          'pointer-events-auto col-start-2 row-start-1 flex h-[46px] shrink-0 items-center rounded-xl border border-hairline bg-[color:var(--sss-chip)] px-4 backdrop-blur-[10px]',
          transparent && 'scale-[1.15] translate-y-5'
        )}
      >
        <Wordmark size={24} className="text-ink" />
      </Link>

      <div
        className={cn(
          'pointer-events-auto col-start-3 row-start-1 flex items-center justify-end gap-2.5',
          // The landing's phone header carries the mark and nothing
          // else — the footer holds the theme and the switches, and the
          // bag is one tap away on any piece.
          hideControlsOnMobile && 'max-[559px]:hidden'
        )}
      >
        {/* Currency and language only ride the bar, and only on a wide
            window — over the hero they would crowd the shot, and on a
            phone there is no room beside the mark. The footer carries
            the pair everywhere the bar cannot. */}
        {!floating && (
          <>
            <CurrencySwitch
              className="max-md:hidden"
              selectClassName="h-[46px] rounded-xl bg-[color:var(--sss-chip)] backdrop-blur-[10px]"
            />
            <LanguageSwitch className="h-[46px] items-center rounded-full bg-[color:var(--sss-chip)] backdrop-blur-[10px] max-md:hidden" />
          </>
        )}
        {/* Only where there is room for it. On a phone the chip row is
            the mark and the bag — the theme is not a thing a shopper
            reaches for mid-scroll, and the footer carries it at every
            width. */}
        <ThemeToggle className={cn(chipClass, 'max-[559px]:hidden')} />
        {count > 0 && (
          <button
            type="button"
            onClick={onOpenBag}
            aria-label={t('layout.cart_aria', { count })}
            className={cn(chipClass, 'relative')}
          >
            <BagIcon size={20} />
            <span className="absolute -right-1.5 -top-1.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-signal px-1 text-[11px] font-bold leading-none text-on-accent">
              {count}
            </span>
          </button>
        )}
      </div>
    </header>
  )
}

/**
 * 1c · the landing's bar, straight off the "Strip" sheet: one
 * full-width hairline row, the lines on the left, the mark centred in
 * its own column so it holds the middle whatever the two sides weigh,
 * and language / theme / bag on the right.
 *
 * The lines are plain anchors to the catalog rows below — this bar only
 * ever renders on the landing, so a hash is all they need, and it lands
 * the row without a round trip.
 */
function StripNav({ onOpenBag, count }: { onOpenBag: () => void; count: number }) {
  const { t } = useTranslation()

  const lines = [
    // "all" is where the page already stands, so it wears the ink the
    // sheet gives the current line.
    { href: '#shop', label: t('layout.nav.all'), current: true },
    { href: '#shop-apple', label: t('layout.nav.apple') },
    { href: '#shop-airpods_pro_cases', label: t('layout.nav.airpods_pro_cases') },
    { href: '#shop-vape_cases', label: t('layout.nav.vape_cases') },
    { href: '#shop-lighter_cases', label: t('layout.nav.lighter_cases') },
  ]

  return (
    <header className="sticky top-0 z-10 hidden h-19 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center border-b border-hairline bg-background px-8 min-[560px]:grid">
      <nav className="sss-rail flex min-w-0 items-center gap-6.5 overflow-x-auto text-[14px] font-medium text-ink-muted max-[899px]:gap-4">
        {lines.map((line) => (
          <a
            key={line.label}
            href={line.href}
            className={cn(
              'whitespace-nowrap transition-colors duration-[120ms] hover:text-ink',
              line.current ? 'text-ink' : 'text-ink-muted'
            )}
          >
            {line.label}
          </a>
        ))}
      </nav>

      <Link href="/" aria-label={t('layout.brand')} className="justify-self-center">
        <Wordmark size={34} className="text-ink" />
      </Link>

      <div className="flex items-center justify-end gap-5 text-[14px] font-medium max-[899px]:gap-3">
        <PlainLanguageSwitch />
        <ThemeToggle className="flex size-10 cursor-pointer items-center justify-center rounded-xl text-ink transition-colors duration-200 hover:text-ink-muted" />
        {count > 0 && (
          <button
            type="button"
            onClick={onOpenBag}
            aria-label={t('layout.cart_aria', { count })}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-ink transition-colors duration-[120ms] hover:text-ink-muted"
          >
            <BagIcon size={20} />
            {t('layout.nav.bag')} ({count})
          </button>
        )}
      </div>
    </header>
  )
}

/**
 * The bar's language pair — the sheet writes it as two words, the
 * current one in ink, rather than the segmented pill the footer wears.
 */
function PlainLanguageSwitch() {
  const { locale, locales } = usePage<SharedProps>().props

  return (
    <div className="flex items-center gap-2.5">
      {locales.map((code) => (
        <button
          key={code}
          type="button"
          aria-current={code === locale}
          onClick={() =>
            code !== locale && router.patch('/locale', { locale: code }, { preserveScroll: true })
          }
          className={cn(
            'cursor-pointer transition-colors duration-[120ms]',
            code === locale ? 'text-ink' : 'text-ink-muted hover:text-ink'
          )}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

function BackHeader({ onOpenBag, count }: { onOpenBag: () => void; count: number }) {
  const { t } = useTranslation()

  return (
    <>
      {/* Three balanced slots so the mark stays optically centred.
          Arrow only — the label just repeated what the icon says, and
          it wrapped awkwardly in Russian. */}
      <Link
        href="/"
        aria-label={t('layout.nav.back')}
        className="flex h-11 flex-1 basis-0 items-center text-ink-muted transition-colors duration-[120ms] hover:text-ink"
      >
        <ArrowLeftIcon size={20} className="shrink-0" />
      </Link>

      <Link href="/" aria-label={t('layout.brand')} className="shrink-0">
        <Wordmark size={26} className="text-ink" />
      </Link>

      <div className="flex flex-1 basis-0 justify-end">
        <BagButton count={count} onClick={onOpenBag} />
      </div>
    </>
  )
}

/** Bag icon with the count badge — white pill, inverse digits. */
function BagButton({ count, onClick }: { count: number; onClick: () => void }) {
  const { t } = useTranslation()

  if (count === 0) return null

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('layout.cart_aria', { count })}
      className={cn(
        'relative flex size-11 cursor-pointer items-center justify-end text-ink md:size-auto',
        'transition-colors duration-[120ms] hover:text-ink-muted',
        'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_5px_var(--sss-ring)]'
      )}
    >
      <BagIcon size={20} className="max-md:size-[22px]" />
      {count > 0 && (
        <span className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-ink px-1 text-[10px] font-bold leading-none text-ink-inverse max-md:right-0 max-md:top-2">
          {count}
        </span>
      )}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/* Language switch                                                     */
/* ------------------------------------------------------------------ */

/**
 * Spec 07 · Language switch — a segmented pair, active half filled
 * with ink. Persists the choice in the Rails session (PATCH /locale)
 * and lets the redirect re-render the page with the new translations.
 */
function LanguageSwitch({ onDark = false, className }: { onDark?: boolean; className?: string }) {
  const { locale, locales } = usePage<SharedProps>().props

  const switchTo = (next: string) => {
    if (!next || next === locale) return
    router.patch('/locale', { locale: next }, { preserveScroll: true })
  }

  return (
    <ToggleGroup
      type="single"
      value={locale}
      onValueChange={switchTo}
      aria-label="Language"
      className={cn(onDark && 'border-[rgba(245,245,245,0.28)] bg-transparent', className)}
    >
      {locales.map((code) => (
        <ToggleGroupItem
          key={code}
          value={code}
          className={
            onDark
              ? 'px-4 py-2.5 text-ink-muted hover:text-ink data-[state=on]:bg-ink data-[state=on]:text-ink-inverse'
              : undefined
          }
        >
          {code.toUpperCase()}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */

/**
 * A centered brand card followed by navigation, preferences and seller details.
 */
function SiteFooter() {
  const { t } = useTranslation()
  const { seller, locale, currency } = usePage<SharedProps>().props
  const collectionQuery = new URLSearchParams({ locale, currency: currency.current }).toString()

  const links = [
    { href: '/', label: t('layout.footer.all_products') },
    { href: '/about', label: t('layout.footer.about') },
    { href: `/collections/airpods-max-charging-stands?${collectionQuery}`, label: t('layout.footer.airpods_max_stands') },
    { href: `/collections/custom-airpods-pro-cases?${collectionQuery}`, label: t('layout.footer.airpods_pro_custom_cases') },
    { href: `/collections/designer-airpods-pro-cases?${collectionQuery}`, label: t('layout.footer.airpods_pro_designer_cases') },
    { href: '/delivery', label: t('layout.footer.shipping') },
    { href: '/returns', label: t('layout.footer.returns') },
    // One payment arrangement on both language versions.
    ...(seller ? [{ href: '/payment', label: t('layout.footer.payment') }] : []),
    { href: 'mailto:support@sickstuff.shop', label: t('layout.footer.contact') },
    { href: '/terms', label: t('layout.footer.terms') },
    { href: '/privacy', label: t('layout.footer.privacy') },
    { href: 'https://www.instagram.com/sick.stuff.shop/', label: t('layout.footer.instagram') },
  ]

  const footerLink = 'text-ink-faint transition-colors duration-[120ms] hover:text-ink'

  return (
    <footer className="bg-background px-8 pb-8 pt-4 max-[559px]:px-4">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-4 text-[13px] text-ink-faint">
        <div className="flex min-h-[180px] flex-col items-center justify-center gap-4 rounded-[18px] border border-dashed border-hairline px-4 py-8 text-center">
          <Wordmark className="text-[clamp(40px,5vw,64px)] text-ink" />
          <span className="text-[13px] text-ink-faint">
            {t('layout.footer.copyright', { year: new Date().getFullYear() })}
          </span>
        </div>

        <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {links.map((link) =>
            link.href === '/about' || link.href.startsWith('/collections/') || link.href.startsWith('http') || link.href.startsWith('mailto:') ? (
              <a key={link.label} href={link.href} className={footerLink}>
                {link.label}
              </a>
            ) : (
              <Link key={link.label} href={link.href} className={footerLink}>
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Every control the header sheds ends up here. The landing's
            phone header keeps only the mark, so this row is the one
            place on that page a shopper can change the theme, the
            language or the currency — which is why it shows at every
            width rather than standing in for a bar that isn't there. */}
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSwitch />
          <CurrencySwitch />
          <ThemeToggle className="size-[42px] rounded-full border border-hairline" />
        </div>

        <SellerDetails />
      </div>
    </footer>
  )
}
